/**
 * "Share with a Hub user": pick one of your accepted connections (or find them in the directory), add an optional
 * note, and the server copies this person's card to them as a pending share. Only connections can receive a share,
 * so a directory match you are not connected with is shown with the reason instead of a button.
 */
import { useEffect, useState } from "react";
import { Search, Share2 } from "lucide-react";
import type { HubUserProfile } from "@hub/shared";
import { Avatar, Button, Field, Input, Modal, Pill, Spinner, Textarea, toast } from "@/ui";
import { useSession } from "../_shims";
import { useConnections, useCreateShare, useDirectorySearch } from "../lib/hooks";
import s from "../network.module.css";

export function ShareContactSheet({ contactId, contactName, open, onClose }: { contactId: string; contactName: string; open: boolean; onClose: () => void }) {
  const { user } = useSession();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<HubUserProfile | null>(null);
  const [note, setNote] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebounced("");
      setPicked(null);
      setNote("");
    }
  }, [open]);
  const connections = useConnections(open);
  const directory = useDirectorySearch(debounced, open);
  const share = useCreateShare();

  const connected = (connections.data?.connections ?? []).filter((c) => c.status === "accepted").map((c) => c.otherUser);
  const q = query.trim().toLowerCase();
  const matching = q ? connected.filter((p) => p.displayName.toLowerCase().includes(q) || (p.headline ?? "").toLowerCase().includes(q)) : connected;
  const others = (directory.data?.users ?? []).filter((p) => p.userId !== user?.id && !connected.some((c) => c.userId === p.userId));
  const rows: { profile: HubUserProfile; connected: boolean }[] = [...matching.map((p) => ({ profile: p, connected: true })), ...others.map((p) => ({ profile: p, connected: false }))];

  const submit = () => {
    if (!picked) return;
    share.mutate(
      { toUserId: picked.userId, contactId, message: note.trim() || null },
      {
        onSuccess: () => {
          toast(`Shared ${contactName} with ${picked.displayName}. They decide whether to accept.`, "success");
          onClose();
        },
        onError: (e) => toast(e instanceof Error ? e.message : "Could not share.", "danger"),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Share ${contactName} with a Hub user`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" icon={<Share2 size={14} />} onClick={submit} disabled={!picked} loading={share.isPending} data-testid="share-submit">
            {picked ? `Share with ${picked.displayName.split(" ")[0]}` : "Share"}
          </Button>
        </>
      }
    >
      <div className="u-col" style={{ gap: 12 }} data-testid="share-sheet">
        <div className={`${s.small} ${s.muted}`}>
          They see the share under Network, Shares and choose whether to add {contactName} to their people. Only the card travels: name, how to reach them, where they work. Your notes and history stay with you.
        </div>
        <div className="u-row" style={{ gap: 6 }}>
          <Search size={14} className="u-muted" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a connection, or search people on Hub" aria-label="Find a Hub user" autoFocus />
        </div>
        {connections.isLoading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <div className={`${s.small} ${s.muted}`} data-testid="share-empty">
            {connected.length === 0 && !q ? "You have no connections yet. Connect with people on the Network page first; Hub shares only between connections." : directory.isFetching ? "Searching Hub." : "Nobody matches that."}
          </div>
        ) : (
          <div className={s.stack} role="listbox" aria-label="Hub users">
            {rows.map(({ profile, connected: isConnected }) => {
              const selected = picked?.userId === profile.userId;
              return (
                <button
                  key={profile.userId}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  disabled={!isConnected}
                  onClick={() => setPicked(profile)}
                  className={s.connRow}
                  data-testid={`share-user-${profile.userId}`}
                  style={{ textAlign: "left", cursor: isConnected ? "pointer" : "default", border: selected ? "1px solid var(--accent)" : "1px solid var(--border)", background: selected ? "var(--bg-sunken)" : undefined, opacity: isConnected ? 1 : 0.7 }}
                >
                  <Avatar name={profile.displayName} src={profile.avatarUrl} size={32} seed={profile.userId} />
                  <div style={{ minWidth: 0 }} className="u-grow">
                    <div className={s.profileName}>{profile.displayName}</div>
                    {(profile.headline || profile.location) && <div className={`${s.small} ${s.muted}`}>{profile.headline ?? profile.location}</div>}
                  </div>
                  {isConnected ? <Pill tone={selected ? "accent" : "success"}>{selected ? "Chosen" : "Connected"}</Pill> : <Pill tone="neutral" title="Hub shares people only between connections. Connect with them on the Network page first.">Not connected</Pill>}
                </button>
              );
            })}
          </div>
        )}
        {picked && (
          <Field label="A note for them (optional)" hint="Why you think they should know each other. Sent as written, nothing else.">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={`${contactName} would be a good person for you to meet because...`} aria-label="Note" />
          </Field>
        )}
      </div>
    </Modal>
  );
}
